import { addRefusal } from '@theme/cart-actions';

/**
 * ⭐ THE ANSWER TO "no visual feedback after adding something to cart currently?"
 *
 * Client, 2026-09-08. They are right, and this module is the whole of the fix.
 *
 * ── WHAT ACTUALLY HAPPENED BEFORE THIS, DRIVEN NOT ASSUMED ───────────────────
 * Both add paths were driven in a browser against the dev server on 2026-09-08 with
 * MutationObservers on the button, the dialog, the header count and every live region.
 * The two paths did not agree, and one of them did nothing a shopper could see.
 *
 * PDP — `/products/bomber-coat-rusty-red`, click `.rp-atc__button`:
 *     t=0ms      `data-added="true"` set on the button by `product-form.js:144`
 *     t=48ms     `shopify:cart:lines-update` from `product-form-component`
 *     t=679ms    header text `cart (5)` → `cart (6)`
 *     t=679ms    PDP live region ← "Added"
 *     t=679ms    header live region ← "Total items in cart: 6"
 *     t=803ms    `data-added` removed
 *     — the cart drawer NEVER OPENED. `drawer.hasAttribute('open')` false throughout.
 *
 *   ✅ CONFIRMS the previous reviewer's passing note that "add-to-cart did not open the
 *   cart drawer on the PDP I drove". The cause is not a bug, it is a SETTING:
 *   `config/settings_schema.json:1567` declares `auto_open_cart_drawer` with
 *   `"default": false` and `config/settings_data.json` never sets it, so
 *   `snippets/cart-drawer.liquid:127` renders `<cart-drawer-component>` with no
 *   `auto-open` attribute and `cart-drawer.js:69` short-circuits. Verified on the live
 *   PDP: `document.querySelector('cart-drawer-component').attributes` → `[]`.
 *
 *   And `data-added` buys nothing. `product-form.js` sets it for ~800ms, but
 *   `[data-added]` is styled only for Horizon's `.add-to-cart-button`
 *   (`snippets/add-to-cart-button-styles.liquid:81,118,122`). Our button is
 *   `.rp-atc__button` and `src/u3-pdp-form.css` has no `[data-added]` rule at all, so
 *   the attribute comes and goes with ZERO pixels changed. The only thing that moved
 *   on that entire page was a 15px count in the far corner, 679ms after the click.
 *
 * CARD quick-add — `/collections/all`, click a size in `form[data-rp-add]`:
 *     t=2ms      `aria-disabled="true"` on the size button
 *     t=38ms     `shopify:cart:lines-update` from `theme-drawer`
 *     t=563ms    `theme-drawer:open` — THE DRAWER OPENS, `:modal` true, focus moves in
 *     t=564ms    header `cart (6)` → `cart (7)`, live region ← "Total items in cart: 7"
 *
 * So the card already confirmed and the PDP did not. Same shopper, same site, same
 * action, two different answers — which is the divergence this module removes by
 * making ONE place decide.
 *
 * ── ⭐ WHAT THE ARTBOARDS DRAW: NOTHING. STATED, AS THE BRIEF ASKS ───────────
 * There is NO confirmation state anywhere in `docs/canvas/`. Checked across all
 * sixteen artboards, not inferred:
 *
 *   - The add button is drawn in exactly ONE state on every board that has it.
 *     `Product.dc.html` and `MobilePDP.dc.html` and `SoldOut.dc.html` all draw
 *     `…text-align:center;…>in winkelwagen</div>` and nothing else — no second
 *     state, no checkmark, no label swap.
 *   - `grep -Ei '(toast|snackbar|banner|success|geslaagd|gelukt|bevestig|melding|
 *     notif|alert|✓|✔|checkmark|popup)' docs/canvas/*.dc.html` returns TWO hits in
 *     the whole set and neither is one: `Collection.dc.html` matches "check" inside
 *     the product name *comfy fit pants — mini check*, and `SoldOut.dc.html` matches
 *     "notif" inside its notify-me prose.
 *   - The two occurrences of "toegevoegd" (`CartDrawer.dc.html`, `CartPage.dc.html`)
 *     are the free dust-bag gift line — *"Gratis dust bag toegevoegd"* — not an
 *     add-to-cart confirmation.
 *
 * So NOTHING IS INVENTED HERE, because the design already contains the answer:
 * `docs/canvas/CartDrawer.dc.html` IS the confirmation. It draws the drawer with
 * `winkelwagen (3)`, the line items, the thresholds and the total. The card path was
 * already using it. The smallest thing consistent with the site's vocabulary is to
 * make the PDP use the same drawn, approved surface — not to add an animation the
 * design does not have.
 *
 * ── WHY NOT JUST TICK `auto_open_cart_drawer` ────────────────────────────────
 * Because Horizon's auto-open is OPTIMISTIC and this unit is required to behave when
 * the request fails. `cart-drawer.js:78` opens the drawer on the EVENT, before the
 * mutation has resolved:
 *
 *     if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) this.#themeDrawer?.open();
 *
 * The `userErrors` guard further down runs ~500ms later, on a drawer that is already
 * on screen. A refused add would therefore slide the cart open showing the OLD cart
 * and no explanation — feedback that says "added" when nothing was. ADR-006 bars
 * editing `cart-drawer.js` to fix that, so the setting stays off and the drawer is
 * opened from here instead, AFTER the mutation is known to have succeeded.
 *
 * ── AND IT HAS TO BE VERIFIED, NOT ASSUMED ───────────────────────────────────
 * A refused add resolves with an EMPTY `userErrors` and reports the refusal in
 * `warnings` — measured, with the numbers, in `addRefusal`'s doc block in
 * `assets/cart-actions.js`. `addRefusal` is the shared test; this module and
 * `updateCartLines` both call it and share one cart read, so the drawer opens only
 * when something really went in.
 *
 * ── ANNOUNCEMENT ─────────────────────────────────────────────────────────────
 * Nothing new is announced from here, deliberately — a third live region shouting
 * over the two that already work would be worse, not better. What a screen reader
 * gets today, verified in the runs above:
 *   - `header-actions.js:71` writes "Total items in cart: N" into its `role="status"`
 *     region on EVERY add, from either path.
 *   - `product-form.js:530` writes `Theme.translations.added` into the PDP's
 *     `aria-live="assertive"` region.
 *   - Opening the drawer is itself an AT event: `<dialog>.showModal()` moves focus
 *     into a modal labelled by `#cart-drawer-heading`, which reads `cart (7)`.
 * On a REFUSED add the first is now correct (the count does not move) and the second
 * now says the refusal instead of "Added", because `addRefusal` puts the message into
 * the `userErrors` array `product-form.js:405` already reads. That fix is at the
 * chokepoint, not here.
 *
 * ── MOTION ───────────────────────────────────────────────────────────────────
 * This module animates NOTHING. It calls `openCart()`; the drawer's own entry
 * transition lives in `src/u2-cart-drawer.css`, bound to `[open]`, and is already
 * scaled by `--motion-scale` (`src/app.css:537-549` — `html[data-motion="off"]` sets
 * `.0001`) and clamped by the blanket motion-off reset. There is no new duration to
 * gate and no second detection site, which is what `AGENTS.md` §Motion asks for.
 */

/** Adds that came from inside the drawer need no drawer-open; it is already there. */
function drawerElement() {
  return /** @type {HTMLElement & {open?: () => void, isOpen?: boolean} | null} */ (
    document.querySelector('theme-drawer#cart-drawer')
  );
}

/**
 * Opens the cart through the Standard Action, so an app that has configured `openCart`
 * still sees it — `assets/standard-actions-override.js` is what points it at our drawer.
 *
 * Guarded on the drawer EXISTING. Without a drawer the override's handler falls back to
 * `window.location.href = cart_url`, so calling it on the cart template — or with
 * `settings.cart_type` set to page — would turn every add into a full navigation.
 */
function openCart() {
  const drawer = drawerElement();
  if (!drawer || drawer.isOpen || drawer.hasAttribute('open')) return;

  const open = window.Shopify?.actions?.openCart;
  if (typeof open === 'function') {
    open();
    return;
  }

  drawer.open?.();
}

/**
 * @param {any} event - `CartLinesUpdateEvent`.
 */
async function onCartLinesUpdate(event) {
  if (event?.action !== 'add' || !event.promise) return;

  // The source is a modal dialog (quick-add). Its own close restores focus, and opening
  // the drawer over it would capture the wrong `#previouslyFocused`. Same deferral
  // `cart-drawer.js:70-80` makes, and for the same reason. A NON-modal dialog does not
  // close on add and does not move focus, so `:modal` is the right test.
  const sourceModal = /** @type {HTMLDialogElement | null} */ (
    event.target instanceof Element ? event.target.closest('dialog:modal') : null
  );

  // Not the cart drawer itself — an add made from inside the open drawer (the
  // cross-sell pill, `rp-cart-add.js`) must not be treated as a dialog to wait on.
  const deferUntilClosed = sourceModal && !sourceModal.closest('theme-drawer#cart-drawer');

  /** @type {any} */
  let result;
  try {
    result = await event.promise;
  } catch (error) {
    // A rejected mutation is a failure. The component that made it owns the message.
    if (/** @type {any} */ (error)?.name !== 'AbortError') console.warn('[rp-cart-feedback] Add rejected:', error);
    return;
  }

  if (result?.userErrors?.length || result?.detail?.didError) return;

  // `event.lines` is what the mutation asked for — present on both the action-emitted
  // event and `cart-actions.js`'s fallback event.
  const refusal = await addRefusal(result, event.lines ?? []);
  if (refusal) return;

  if (deferUntilClosed && sourceModal?.open) {
    sourceModal.addEventListener('close', () => openCart(), { once: true });
    return;
  }

  openCart();
}

document.addEventListener('shopify:cart:lines-update', onCartLinesUpdate);
